import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";
import { STAGES } from "@/sales/lib/api";
import DealCard from "@/sales/components/DealCard";
import { Plus } from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";
import { formatEuro } from "@/sales/lib/api";

export default function KanbanBoard({
  deals,
  loading,
  onStageChange,
  onCardClick,
  onAddLead,
  readOnly = false,
}) {
  const handleDragEnd = (result) => {
    if (readOnly) return;
    const { destination, source, draggableId } = result;
    if (!destination) return;
    if (destination.droppableId === source.droppableId && destination.index === source.index) return;

    // Determine outcome for stage 5
    let outcome = null;
    if (destination.droppableId === "gewonnen-won") {
      outcome = "gewonnen";
    } else if (destination.droppableId === "gewonnen-lost") {
      outcome = "verloren";
    }

    const stageId = destination.droppableId.startsWith("gewonnen") ? "gewonnen" : destination.droppableId;
    onStageChange(draggableId, stageId, outcome);
  };

  const handleCardClick = (deal) => {
    if (readOnly) return;
    onCardClick(deal);
  };

  const groupBy = (stageId, outcome = null) => {
    return deals.filter((d) => {
      if (stageId !== "gewonnen") return d.stage === stageId;
      if (outcome === "gewonnen") return d.stage === "gewonnen" && d.outcome !== "verloren";
      if (outcome === "verloren") return d.stage === "gewonnen" && d.outcome === "verloren";
      return d.stage === "gewonnen";
    });
  };

  const sumValue = (list) => list.reduce((acc, d) => acc + (d.value || 0), 0);

  if (loading) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-4">
        {[0, 1, 2, 3, 4].map((i) => (
          <Skeleton key={i} className="h-[60vh] bg-[#142539]/60 rounded-lg" />
        ))}
      </div>
    );
  }

  return (
    <DragDropContext onDragEnd={handleDragEnd}>
      <div
        className="flex gap-4 overflow-x-auto pb-4 kanban-scroll"
        data-testid="kanban-board"
      >
        {STAGES.map((stage, idx) => {
          if (stage.id === "gewonnen") {
            const wonList = groupBy("gewonnen", "gewonnen");
            const lostList = groupBy("gewonnen", "verloren");
            return (
              <ClosureColumn
                key={stage.id}
                stage={stage}
                wonList={wonList}
                lostList={lostList}
                onCardClick={handleCardClick}
              />
            );
          }
          const list = groupBy(stage.id);
          return (
            <Column
              key={stage.id}
              stage={stage}
              deals={list}
              total={sumValue(list)}
              onCardClick={handleCardClick}
              onAddLead={idx === 0 && !readOnly ? onAddLead : null}
            />
          );
        })}
      </div>
    </DragDropContext>
  );
}

function Column({ stage, deals, total, onCardClick, onAddLead }) {
  return (
    <div
      className="min-w-[300px] w-[300px] flex-shrink-0 flex flex-col bg-[#142539]/70 rounded-lg border border-[rgba(192,192,192,0.12)]"
      data-testid={`column-${stage.id}`}
    >
      <ColumnHeader stage={stage} count={deals.length} total={total} />
      {onAddLead && (
        <button
          onClick={onAddLead}
          data-testid="column-add-lead-btn"
          className="mx-3 mb-2 flex items-center justify-center gap-1.5 text-xs uppercase tracking-[0.18em] text-[#94A3B8] hover:text-[#C0C0C0] border border-dashed border-[rgba(192,192,192,0.2)] hover:border-[rgba(192,192,192,0.5)] rounded-md py-2 transition-all"
        >
          <Plus size={14} />
          Voeg Organisatie Toe
        </button>
      )}
      <Droppable droppableId={stage.id}>
        {(provided, snapshot) => (
          <div
            ref={provided.innerRef}
            {...provided.droppableProps}
            className={`flex-1 px-3 pb-3 min-h-[140px] transition-colors ${
              snapshot.isDraggingOver ? "bg-[#1C3A5A]/40" : ""
            }`}
          >
            {deals.map((deal, index) => (
              <Draggable key={deal.id} draggableId={deal.id} index={index}>
                {(prov, snap) => (
                  <div
                    ref={prov.innerRef}
                    {...prov.draggableProps}
                    {...prov.dragHandleProps}
                    style={prov.draggableProps.style}
                  >
                    <DealCard deal={deal} isDragging={snap.isDragging} onClick={() => onCardClick(deal)} />
                  </div>
                )}
              </Draggable>
            ))}
            {provided.placeholder}
            {deals.length === 0 && !snapshot.isDraggingOver && (
              <div className="text-center text-xs text-[#64748B] py-6 italic">
                Sleep deals hier
              </div>
            )}
          </div>
        )}
      </Droppable>
    </div>
  );
}

function ClosureColumn({ stage, wonList, lostList, onCardClick }) {
  const total = wonList.reduce((a, d) => a + (d.value || 0), 0) + lostList.reduce((a, d) => a + (d.value || 0), 0);
  return (
    <div
      className="min-w-[340px] w-[340px] flex-shrink-0 flex flex-col bg-[#142539]/70 rounded-lg border border-[rgba(192,192,192,0.12)]"
      data-testid={`column-${stage.id}`}
    >
      <ColumnHeader stage={stage} count={wonList.length + lostList.length} total={total} />
      <div className="flex-1 px-3 pb-3 grid grid-cols-2 gap-2">
        <SubDrop id="gewonnen-won" title="Gewonnen" tint="emerald" list={wonList} onCardClick={onCardClick} />
        <SubDrop id="gewonnen-lost" title="Verloren" tint="red" list={lostList} onCardClick={onCardClick} />
      </div>
    </div>
  );
}

function SubDrop({ id, title, tint, list, onCardClick }) {
  const color = tint === "emerald" ? "text-emerald-400" : "text-rose-400";
  const bg =
    tint === "emerald"
      ? "border-emerald-500/20 bg-emerald-900/10"
      : "border-rose-500/20 bg-rose-900/10";
  return (
    <Droppable droppableId={id}>
      {(provided, snapshot) => (
        <div
          ref={provided.innerRef}
          {...provided.droppableProps}
          className={`rounded-md border ${bg} min-h-[180px] p-2 transition-colors ${
            snapshot.isDraggingOver ? "ring-1 ring-[#C0C0C0]/40" : ""
          }`}
          data-testid={`subcolumn-${id}`}
        >
          <p className={`text-[10px] uppercase tracking-[0.2em] font-semibold mb-2 ${color}`}>
            {title} <span className="text-[#64748B] font-normal ml-1">({list.length})</span>
          </p>
          {list.map((deal, index) => (
            <Draggable key={deal.id} draggableId={deal.id} index={index}>
              {(prov, snap) => (
                <div
                  ref={prov.innerRef}
                  {...prov.draggableProps}
                  {...prov.dragHandleProps}
                  style={prov.draggableProps.style}
                >
                  <DealCard deal={deal} isDragging={snap.isDragging} compact onClick={() => onCardClick(deal)} />
                </div>
              )}
            </Draggable>
          ))}
          {provided.placeholder}
        </div>
      )}
    </Droppable>
  );
}

function ColumnHeader({ stage, count, total }) {
  return (
    <div className="px-4 pt-4 pb-3 border-b border-[rgba(192,192,192,0.1)]">
      <div className="flex items-center justify-between mb-1">
        <div className="flex items-center gap-2">
          <span className="font-serif-display text-[#C0C0C0] text-lg leading-none">
            {stage.number}.
          </span>
          <h3 className="font-serif-display text-base font-medium text-[#F8FAFC] leading-tight">
            {stage.short}
          </h3>
        </div>
        <span className="text-[10px] uppercase tracking-wider text-[#94A3B8] bg-[#0B1521]/60 border border-[rgba(192,192,192,0.15)] rounded-full px-2 py-0.5">
          {count}
        </span>
      </div>
      <p className="text-[10px] uppercase tracking-[0.16em] text-[#64748B]">
        {stage.sub ? `${stage.label} · ${stage.sub}` : stage.label}
      </p>
      <p className="text-xs text-[#C0C0C0] mt-1.5 font-medium tabular-nums">
        {formatEuro(total)}
      </p>
    </div>
  );
}
